
// File: frontend/src/modules/trading/dispatch_order/pages/TradingDispatchOrderListPage.js

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import tradingDispatchOrderService from "../services/tradingDispatchOrderService";

const styles = {
  page: {
    padding: "24px",
    background: "#f7f8fc",
    minHeight: "100%",
    fontFamily: "Inter, Arial, sans-serif",
    color: "#252b3b",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    marginBottom: "22px",
  },
  title: { margin: 0, fontSize: "25px", fontWeight: 700 },
  subtitle: {
    margin: "7px 0 0",
    color: "#747b8d",
    fontSize: "13px",
  },
  button: {
    border: 0,
    borderRadius: "8px",
    padding: "11px 17px",
    background: "#6254d9",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
  secondaryButton: {
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    padding: "10px 14px",
    background: "#fff",
    color: "#374151",
    fontWeight: 600,
    cursor: "pointer",
  },
  card: {
    background: "#fff",
    border: "1px solid #e8eaf2",
    borderRadius: "12px",
    padding: "18px",
    marginBottom: "18px",
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "13px",
    marginBottom: "16px",
  },
  statCard: {
    position: "relative",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    gap: "14px",
    minHeight: "93px",
    boxSizing: "border-box",
    borderRadius: "12px",
    padding: "20px 16px",
    color: "#fff",
    boxShadow: "0 5px 12px rgba(15, 23, 42, 0.10)",
  },
  statAccentTotal: {
    background: "linear-gradient(115deg, #19a7f5 0%, #0860e8 100%)",
  },
  statAccentDraft: {
    background: "linear-gradient(115deg, #a47bff 0%, #7738df 100%)",
  },
  statAccentPending: {
    background: "linear-gradient(115deg, #ffb51b 0%, #ff8500 100%)",
  },
  statAccentApproved: {
    background: "linear-gradient(115deg, #39c985 0%, #159957 100%)",
  },
  statIcon: {
    width: "46px",
    height: "46px",
    minWidth: "46px",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.23)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "22px",
  },
  statContent: { position: "relative", zIndex: 1 },
  statLabel: {
    color: "#fff",
    fontSize: "11px",
    fontWeight: 700,
    marginBottom: "7px",
  },
  statValue: {
    fontSize: "27px",
    fontWeight: 750,
    lineHeight: 1.1,
    color: "#fff",
    fontVariantNumeric: "tabular-nums",
  },
  decorativeShape: {
    position: "absolute",
    width: "125px",
    height: "80px",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.12)",
    right: "-24px",
    bottom: "-43px",
    transform: "rotate(-20deg)",
    pointerEvents: "none",
  },
  filters: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "16px",
  },
  input: {
    minWidth: "220px",
    flex: 1,
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    padding: "11px 12px",
    fontSize: "13px",
    outline: "none",
    boxSizing: "border-box",
  },
  select: {
    minWidth: "170px",
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    padding: "10px 12px",
    background: "#fff",
    fontSize: "13px",
  },
  tableWrap: { width: "100%", overflowX: "auto" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1050px",
  },
  th: {
    textAlign: "left",
    padding: "13px 12px",
    background: "#f7f8fc",
    color: "#70778a",
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: ".4px",
    borderBottom: "1px solid #e8eaf2",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "14px 12px",
    fontSize: "13px",
    borderBottom: "1px solid #f0f1f6",
    whiteSpace: "nowrap",
  },
  link: {
    color: "#5548cb",
    fontWeight: 700,
    cursor: "pointer",
    border: 0,
    padding: 0,
    background: "transparent",
  },
  error: {
    padding: "12px 14px",
    background: "#fff0f0",
    color: "#b42318",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  muted: {
    color: "#858b9b",
    fontSize: "13px",
    textAlign: "center",
    padding: "32px 12px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    marginTop: "15px",
    color: "#747b8d",
    fontSize: "12px",
  },
};

const normalizeStatus = (value) =>
  String(value || "DRAFT").trim().toUpperCase();

const formatStatus = (value) =>
  normalizeStatus(value).replace(/_/g, " ");

const formatDate = (value) => {
  if (!value) return "—";
  const raw = String(value).slice(0, 10);
  const parts = raw.split("-");
  return parts.length === 3
    ? `${parts[2]}-${parts[1]}-${parts[0]}`
    : raw;
};

const getResponseData = (response) =>
  response?.data?.data ?? response?.data ?? {};

const statusColors = {
  DRAFT: { background: "#efedff", color: "#5548cb" },
  PENDING_APPROVAL: { background: "#fff4d6", color: "#946200" },
  APPROVED: { background: "#dcfce7", color: "#166534" },
  REJECTED: { background: "#fee2e2", color: "#b42318" },
  CANCELLED: { background: "#f1f3f9", color: "#5b6172" },
};

function StatusBadge({ status }) {
  const colors = statusColors[normalizeStatus(status)] || {
    background: "#f1f3f9",
    color: "#5b6172",
  };

  return (
    <span
      style={{
        display: "inline-block",
        borderRadius: "20px",
        padding: "5px 9px",
        fontSize: "11px",
        fontWeight: 700,
        whiteSpace: "nowrap",
        ...colors,
      }}
    >
      {formatStatus(status)}
    </span>
  );
}

export default function TradingDispatchOrderListPage() {
  const navigate = useNavigate();

  const [dispatchOrders, setDispatchOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const loadDispatchOrders = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (statusFilter !== "ALL") params.status_code = statusFilter;

      const response =
        await tradingDispatchOrderService.getDispatchOrders(params);

      const body = getResponseData(response);
      const rows = Array.isArray(body)
        ? body
        : Array.isArray(body?.items)
          ? body.items
          : [];

      setDispatchOrders(rows);
    } catch (err) {
      setDispatchOrders([]);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to load Trading Dispatch Orders."
      );
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    loadDispatchOrders();
  }, [loadDispatchOrders]);

  const statuses = [
    "DRAFT",
    "PENDING_APPROVAL",
    "APPROVED",
    "REJECTED",
  ];

  const drafts = dispatchOrders.filter(
    (row) => normalizeStatus(row.status_code) === "DRAFT"
  ).length;

  const pendingApproval = dispatchOrders.filter(
    (row) => normalizeStatus(row.status_code) === "PENDING_APPROVAL"
  ).length;

  const approved = dispatchOrders.filter(
    (row) => normalizeStatus(row.status_code) === "APPROVED"
  ).length;

  const openDispatchOrder = (row) => {
    if (row?.id) {
      navigate(`/trading/dispatch-orders/${row.id}`);
    }
  };

  const createDispatchOrder = () => {
    navigate("/trading/dispatch-orders/create");
  };

  const statCards = [
    {
      label: "Dispatch Orders",
      value: dispatchOrders.length,
      icon: "fas fa-list-alt",
      accent: styles.statAccentTotal,
    },
    {
      label: "Draft",
      value: drafts,
      icon: "fas fa-file-alt",
      accent: styles.statAccentDraft,
    },
    {
      label: "Pending Approval",
      value: pendingApproval,
      icon: "fas fa-hourglass-half",
      accent: styles.statAccentPending,
    },
    {
      label: "Approved",
      value: approved,
      icon: "fas fa-check-circle",
      accent: styles.statAccentApproved,
    },
  ];

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Trading Dispatch Orders</h1>
          <p style={styles.subtitle}>
            Create dispatch plans from approved Sales Orders and track their
            approval status.
          </p>
        </div>

        <button
          type="button"
          style={styles.button}
          onClick={createDispatchOrder}
        >
          + Create Dispatch Order
        </button>
      </div>

      {error && (
        <div role="alert" style={styles.error}>
          {error}
          <button
            type="button"
            onClick={loadDispatchOrders}
            style={{
              marginLeft: "12px",
              border: 0,
              background: "transparent",
              color: "#b42318",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      )}

      <div style={styles.stats}>
        {statCards.map((card) => (
          <div
            key={card.label}
            style={{ ...styles.statCard, ...card.accent }}
          >
            <div style={styles.statIcon}>
              <i className={card.icon} />
            </div>
            <div style={styles.statContent}>
              <div style={styles.statLabel}>{card.label}</div>
              <div style={styles.statValue}>{card.value}</div>
            </div>
            <div style={styles.decorativeShape} />
          </div>
        ))}
      </div>

      <div style={styles.card}>
        <div style={styles.filters}>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search Dispatch Order no. or Sales Order no..."
            style={styles.input}
            aria-label="Search Dispatch Orders"
          />

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            style={styles.select}
            aria-label="Filter Dispatch Orders by status"
          >
            <option value="ALL">All statuses</option>
            {statuses.map((status) => (
              <option key={status} value={status}>
                {formatStatus(status)}
              </option>
            ))}
          </select>

          <button
            type="button"
            style={{
              ...styles.button,
              opacity: loading ? 0.65 : 1,
            }}
            onClick={loadDispatchOrders}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Dispatch Order No.</th>
                <th style={styles.th}>Sales Order No.</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Order Date</th>
                <th style={styles.th}>Planned Dispatch</th>
                <th style={styles.th}>Transport Booking</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} style={styles.muted}>
                    Loading Trading Dispatch Orders...
                  </td>
                </tr>
              ) : dispatchOrders.length === 0 ? (
                <tr>
                  <td colSpan={8} style={styles.muted}>
                    {error
                      ? "Dispatch Orders could not be loaded."
                      : "No Trading Dispatch Orders found."}
                  </td>
                </tr>
              ) : (
                dispatchOrders.map((row) => (
                  <tr key={row.id}>
                    <td style={styles.td}>
                      <button
                        type="button"
                        style={styles.link}
                        onClick={() => openDispatchOrder(row)}
                      >
                        {row.dispatch_order_no || `DO-${row.id}`}
                      </button>
                    </td>

                    <td style={styles.td}>{row.sales_order_no || "—"}</td>

                    <td style={styles.td}>
                      {row.customer_name || "—"}
                    </td>

                    <td style={styles.td}>
                      {formatDate(row.dispatch_order_date)}
                    </td>

                    <td style={styles.td}>
                      {formatDate(row.planned_dispatch_date)}
                    </td>

                    <td style={styles.td}>
                      {row.transporter_booking_required ? "Required" : "No"}
                    </td>

                    <td style={styles.td}>
                      <StatusBadge status={row.status_code} />
                    </td>

                    <td style={styles.td}>
                      <button
                        type="button"
                        style={styles.link}
                        onClick={() => openDispatchOrder(row)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={styles.footer}>
          <span>
            Showing {dispatchOrders.length} Dispatch Order
            {dispatchOrders.length === 1 ? "" : "s"}
          </span>
          <span>
            Use the search and status filters to narrow the list.
          </span>
        </div>
      </div>
    </div>
  );
}
